/**
 * Notification-service InboundAuthConfig endpoints — used
 * to set up HMAC-based inbound authentication.
 * Injected into the shared `integrationApi`.
 *
 *   GET    /v1/inbound/auth/active/{callerId}  → { authConfig: {...} } | {}  (404 → {})
 *   POST   /v1/inbound/auth                    → { id, callerId, ... }  — the created
 *                                                  config UNWRAPPED, no `authConfig` envelope
 *   DELETE /v1/inbound/auth/{id}               → (no meaningful body)
 *
 * GET and POST disagree on envelope shape even though both hand back the
 * same kind of config row. `transformResponse` on the create mutation
 * wraps its row in `{ authConfig }` to match GET's shape, so every caller
 * reads `.authConfig` the same way regardless of which endpoint produced
 * the value — treating the create response as already wrapped is what let
 * a StoreFront plugin's first-ever connect silently hand `undefined` to
 * the plugin-installed snippet.
 */
import { services } from '@config/services';
import { integrationApi } from './_base';

export interface InboundAuthConfigRecord {
  id: string;
  callerId: string;
  hmacAlgo?: string;
  hmacSignatureHeaderName?: string;
  hmacSecret?: string;
  enabled?: boolean;
  merchantId?: string;
  [key: string]: unknown;
}

export interface InboundAuthConfigResponse {
  authConfig?: InboundAuthConfigRecord;
  [key: string]: unknown;
}

export interface CreateInboundAuthConfigBody {
  callerId: string;
  hmacSecret: string;
  hmacAlgo: string;
  hmacSignatureHeaderName: string;
  enabled: boolean;
  merchantId?: string;
}

/** Exported request builders — wire-shape lock tests assert these directly. */
export const inboundAuthRequestBuilders = {
  // GET /notification/v1/inbound/auth/active/{callerId}
  getInboundAuthConfig: (callerId: string) => ({
    url: `${services.notification}/v1/inbound/auth/active/${encodeURIComponent(callerId)}`,
    method: 'GET' as const,
  }),
  // POST /notification/v1/inbound/auth
  createInboundAuthConfig: (body: CreateInboundAuthConfigBody) => ({
    url: `${services.notification}/v1/inbound/auth`,
    method: 'POST' as const,
    body,
  }),
  // DELETE /notification/v1/inbound/auth/{id}
  removeInboundAuthConfig: (id: string) => ({
    url: `${services.notification}/v1/inbound/auth/${encodeURIComponent(id)}`,
    method: 'DELETE' as const,
  }),
};

const inboundAuthApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /notification/v1/inbound/auth/active/{callerId}
     * Returns `{ authConfig }` when found or `{}` when not found (404).
     * The 404→empty case is a normal "not yet configured" signal — NOT an
     * error. */
    getInboundAuthConfig: builder.query<InboundAuthConfigResponse, string>({
      query: (callerId) => inboundAuthRequestBuilders.getInboundAuthConfig(callerId),
      transformResponse: (response: InboundAuthConfigResponse) => response,
      providesTags: (_r, _e, callerId) => [{ type: 'InboundAuthConfig', id: callerId }],
    }),
    /** POST /notification/v1/inbound/auth — create a new InboundAuthConfig.
     * The response is the created row unwrapped; wrap it in `{ authConfig }`
     * so callers read this the same way as the GET response. */
    createInboundAuthConfig: builder.mutation<InboundAuthConfigResponse, CreateInboundAuthConfigBody>({
      query: (body) => inboundAuthRequestBuilders.createInboundAuthConfig(body),
      transformResponse: (response: InboundAuthConfigRecord): InboundAuthConfigResponse => ({
        authConfig: response,
      }),
      invalidatesTags: (_r, _e, { callerId }) => [{ type: 'InboundAuthConfig', id: callerId }],
    }),
    /** DELETE /notification/v1/inbound/auth/{id} — remove an InboundAuthConfig. */
    removeInboundAuthConfig: builder.mutation<unknown, string>({
      query: (id) => inboundAuthRequestBuilders.removeInboundAuthConfig(id),
      transformResponse: (response: unknown) => response,
      invalidatesTags: ['InboundAuthConfig'],
    }),
  }),
});

export const {
  useGetInboundAuthConfigQuery,
  useLazyGetInboundAuthConfigQuery,
  useCreateInboundAuthConfigMutation,
  useRemoveInboundAuthConfigMutation,
} = inboundAuthApi;
